import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, UserPlus, Loader2, KeyRound, Pencil, Check, X, ShieldCheck, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Brand } from "../components/Brand";
import { usersApi, UsuarioCRM, PermisoOverrideCRM } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL, useInicio } from "../lib/permisos";

// Catálogo de rutas/tarjetas sobre las que se puede forzar acceso por
// usuario puntual -- mismo identificador que MenuItem.to en tienda.tsx /
// dashboard.tsx y las rutas de App.tsx, para que un solo override controle
// la tarjeta y la navegación (y, en los pocos endpoints con enforcement
// real, también el backend -- ver @PermisoOverride).
const CATALOGO_RUTAS: { grupo: string; items: { ruta: string; label: string }[] }[] = [
  {
    grupo: "Operación",
    items: [
      { ruta: "/reservas", label: "Reservas" },
      { ruta: "/tienda/lista-espera", label: "Lista de espera" },
      { ruta: "/tienda/walkin", label: "Lista de espera (walk-in)" },
      { ruta: "/tienda/pagos-reservas", label: "Pagos de reservas" },
      { ruta: "/calendario", label: "Calendario" },
      { ruta: "/tienda/ocupacion", label: "Ocupación" },
      { ruta: "/tienda/eventos", label: "Eventos privados" },
      { ruta: "/tienda/tareas", label: "Avisos y tareas" },
      { ruta: "/tienda/bloqueos", label: "Bloqueos" },
    ],
  },
  {
    grupo: "Delivery & catálogo",
    items: [
      { ruta: "/delivery", label: "Delivery" },
      { ruta: "/tienda/menu-config", label: "Menú digital" },
      { ruta: "/articulos-config", label: "Configurar artículos" },
      { ruta: "/tienda/categorias", label: "Categorías" },
      { ruta: "/pasarelas-pago", label: "Pasarelas de pago" },
      { ruta: "/tienda/delivery-zonas", label: "Zonas de delivery" },
    ],
  },
  {
    grupo: "Inteligencia & comunicación",
    items: [
      { ruta: "/cliente360", label: "Cliente 360°" },
      { ruta: "/customers", label: "Base de clientes" },
      { ruta: "/audiencias", label: "Audiencias" },
      { ruta: "/reportes", label: "Reportes" },
      { ruta: "/tienda/qr", label: "Códigos QR" },
      { ruta: "/tienda/email-templates", label: "Plantillas de email" },
      { ruta: "/tienda/whatsapp", label: "WhatsApp Bot" },
      { ruta: "/tienda/encuestas", label: "Encuestas & feedback" },
      { ruta: "/encuestas-qr", label: "Encuestas (QR)" },
    ],
  },
  {
    grupo: "Configuración",
    items: [
      { ruta: "/sucursales", label: "Mi sucursal" },
      { ruta: "/tienda/modulos", label: "Módulos del cliente" },
    ],
  },
];

// Roles que se pueden asignar desde la pantalla
const ROLES_ASIGNABLES = [
  "tienda_recepcion",
  "tienda_supervisor",
  "cocina",
  "operativo",
  "repartidor",
  "admin",
  "gerencia",
  "marketing",
  "ventas",
  "cuentas_cobrar",
  "cuentas_pagar",
  "cajero",
];

function fmtFecha(s?: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("es-VE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function UsuariosPage() {
  const inicio = useInicio();
  const { user: actual } = useAuth();
  const [users, setUsers] = useState<UsuarioCRM[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({ fullName: "", email: "", password: "", role: "tienda_recepcion", telefono: "" });
  const [creando, setCreando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Estado para cambio de contraseña
  const [passUserId, setPassUserId] = useState<string | null>(null);
  const [newPass, setNewPass] = useState("");
  const [passLoading, setPassLoading] = useState(false);
  const [passMsg, setPassMsg] = useState<string | null>(null);

  // Estado para edición de nombre/rol
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [editRol, setEditRol] = useState("");
  const [editTelefono, setEditTelefono] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState<string | null>(null);

  // Estado para panel de permisos (overrides por usuario puntual)
  const [permisosAbiertoId, setPermisosAbiertoId] = useState<string | null>(null);
  const [permisosRows, setPermisosRows] = useState<PermisoOverrideCRM[]>([]);
  const [permisosCargando, setPermisosCargando] = useState(false);
  const [permisosGuardandoRuta, setPermisosGuardandoRuta] = useState<string | null>(null);

  const esAdminVerdadero = !!actual && (actual.superAdmin === true || actual.role === "admin");

  async function togglePermisosPanel(u: UsuarioCRM) {
    if (permisosAbiertoId === u.id) { setPermisosAbiertoId(null); return; }
    setPermisosAbiertoId(u.id);
    setEditUserId(null); setPassUserId(null);
    setPermisosCargando(true);
    try {
      setPermisosRows(await usersApi.listPermisos(u.id));
    } catch (e: any) {
      alert(`Error cargando permisos: ${e.message}`);
    } finally {
      setPermisosCargando(false);
    }
  }

  function overrideDe(ruta: string): boolean | undefined {
    return permisosRows.find(r => r.ruta === ruta)?.otorgado;
  }

  async function cambiarPermiso(userId: string, ruta: string, valor: "heredado" | "si" | "no") {
    setPermisosGuardandoRuta(ruta);
    try {
      if (valor === "heredado") {
        await usersApi.removePermiso(userId, ruta);
        setPermisosRows(rows => rows.filter(r => r.ruta !== ruta));
      } else {
        const otorgado = valor === "si";
        const saved = await usersApi.setPermiso(userId, ruta, otorgado);
        setPermisosRows(rows => [...rows.filter(r => r.ruta !== ruta), saved]);
      }
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setPermisosGuardandoRuta(null);
    }
  }

  async function toggleDelegacion(u: UsuarioCRM) {
    try {
      await usersApi.setGestionarPermisos(u.id, !u.puedeGestionarPermisos);
      await load();
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    }
  }

  async function eliminarUsuario(u: UsuarioCRM) {
    if (!confirm(`¿Eliminar a ${u.fullName} (${u.email})? Esta acción no se puede deshacer.`)) return;
    try {
      await usersApi.remove(u.id);
      await load();
    } catch (e: any) {
      alert(`No se pudo eliminar: ${e.message}`);
    }
  }

  function startEdit(u: UsuarioCRM) {
    setEditUserId(u.id);
    setEditNombre(u.fullName);
    setEditRol(u.role);
    setEditTelefono(u.telefono ?? "");
    setEditMsg(null);
    setPassUserId(null); // cerrar cambio de pass si estaba abierto
  }

  function cancelEdit() {
    setEditUserId(null);
    setEditMsg(null);
  }

  async function guardarEdit() {
    if (!editUserId || !editNombre.trim()) { setEditMsg("El nombre no puede estar vacío"); return; }
    setEditLoading(true);
    setEditMsg(null);
    try {
      await usersApi.update(editUserId, { fullName: editNombre.trim(), role: editRol, telefono: editTelefono.trim() });
      setEditMsg("✓ Guardado");
      setTimeout(() => { setEditUserId(null); setEditMsg(null); }, 1200);
      await load();
    } catch (e: any) {
      setEditMsg(`Error: ${e.message}`);
    } finally {
      setEditLoading(false);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await usersApi.list());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function crear() {
    setFormError(null);
    setOk(null);
    if (!form.fullName.trim()) { setFormError("Ingresá el nombre"); return; }
    if (!form.email.trim()) { setFormError("Ingresá el email"); return; }
    if (form.password.length < 6) { setFormError("La contraseña debe tener al menos 6 caracteres"); return; }

    setCreando(true);
    try {
      const nuevo = await usersApi.create({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        telefono: form.telefono.trim() || undefined,
      });
      setOk(`Usuario ${nuevo.email} creado`);
      setForm({ fullName: "", email: "", password: "", role: "tienda_recepcion", telefono: "" });
      await load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(u: UsuarioCRM) {
    try {
      await usersApi.setActivo(u.id, !u.isActive);
      await load();
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    }
  }

  async function cambiarPassword() {
    if (!passUserId || newPass.length < 6) {
      setPassMsg("Mínimo 6 caracteres");
      return;
    }
    setPassLoading(true);
    setPassMsg(null);
    try {
      await usersApi.resetPassword(passUserId, newPass);
      setPassMsg("Contraseña actualizada");
      setNewPass("");
      setTimeout(() => { setPassUserId(null); setPassMsg(null); }, 2000);
    } catch (e: any) {
      setPassMsg(`Error: ${e.message}`);
    } finally {
      setPassLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Brand size="sm" />
          <Link to={inicio} className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800">
            <ArrowLeft className="size-4" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="font-display text-3xl font-bold text-ink-800 mb-1">Usuarios</h1>
        <p className="text-sm text-ink-500 mb-6">Crear y administrar el acceso del personal al CRM.</p>

        {/* Alta */}
        <div className="card p-5 mb-6">
          <h2 className="text-sm font-semibold text-ink-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <UserPlus className="size-4" /> Nuevo usuario
          </h2>

          {formError && (
            <div className="mb-3 p-2 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded">{formError}</div>
          )}
          {ok && (
            <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded">{ok}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Nombre completo</label>
              <input className="input" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} placeholder="Nombre y apellido" />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="persona@redesip.org" />
            </div>
            <div>
              <label className="label">Contraseña</label>
              <input className="input" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Mínimo 6 caracteres" />
            </div>
            <div>
              <label className="label">Teléfono (WhatsApp, opcional)</label>
              <input className="input" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="0412-1234567" />
            </div>
            <div>
              <label className="label">Rol</label>
              <select className="input" value={form.role} onChange={(e) => set("role", e.target.value)}>
                {ROLES_ASIGNABLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>
                ))}
              </select>
            </div>
          </div>

          <button onClick={crear} disabled={creando} className="btn-primary mt-4 disabled:opacity-60">
            {creando ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
            {creando ? "Creando…" : "Crear usuario"}
          </button>
        </div>

        {/* Lista */}
        <div className="card overflow-x-auto">
          {error && <div className="p-4 bg-rose-50 text-rose-800 text-sm border-b border-rose-200">Error: {error}</div>}
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-left text-ink-600">
              <tr>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Teléfono</th>
                <th className="px-4 py-3 font-semibold">Rol</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3 font-semibold">Último ingreso</th>
                <th className="px-4 py-3 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-500">Cargando…</td></tr>
              )}
              {!loading && users.map((u) => {
                const esYo = u.id === actual?.id;
                const actorEsSuperAdmin = actual?.superAdmin === true;
                const targetProtegido = u.superAdmin || ["admin", "gerencia", "tienda_supervisor"].includes(u.role);
                const noPuedeEliminar = esYo || (!actorEsSuperAdmin && targetProtegido);
                const editandoPass = passUserId === u.id;
                const editando = editUserId === u.id;
                return (
                  <tr key={u.id} className={`border-b border-ink-100 ${editando ? "bg-brand-50" : ""}`}>

                    {/* Nombre — editable inline */}
                    <td className="px-4 py-3 font-medium">
                      {editando ? (
                        <input
                          value={editNombre}
                          onChange={e => setEditNombre(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") guardarEdit(); if (e.key === "Escape") cancelEdit(); }}
                          className="w-full border border-brand-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                          autoFocus
                        />
                      ) : (
                        <span className="text-ink-900">
                          {u.fullName}
                          {u.superAdmin && <span className="ml-2 text-xs text-brand-700">(maestro)</span>}
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-ink-600 text-sm">{u.email}</td>

                    {/* Teléfono — editable inline */}
                    <td className="px-4 py-3 text-ink-600 text-sm">
                      {editando ? (
                        <input
                          value={editTelefono}
                          onChange={e => setEditTelefono(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") guardarEdit(); if (e.key === "Escape") cancelEdit(); }}
                          placeholder="0412-1234567"
                          className="w-full border border-brand-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      ) : (
                        u.telefono || <span className="text-ink-300">—</span>
                      )}
                    </td>

                    {/* Rol — editable inline */}
                    <td className="px-4 py-3">
                      {editando ? (
                        <select
                          value={editRol}
                          onChange={e => setEditRol(e.target.value)}
                          disabled={u.superAdmin && !actual?.superAdmin}
                          className="border border-brand-300 rounded px-2 py-1 text-xs max-w-[150px] focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                          {ROLES_ASIGNABLES.map(r => (
                            <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-ink-700 text-sm">{ROLE_LABEL[u.role] ?? u.role}</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                        {u.isActive ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-500 text-xs">{fmtFecha(u.lastLoginAt)}</td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">

                        {/* Modo edición activa — botones compactos */}
                        {editando ? (
                          <div className="flex items-center gap-1.5">
                            <button onClick={guardarEdit} disabled={editLoading}
                              title="Guardar cambios"
                              className="w-8 h-8 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 flex items-center justify-center shrink-0">
                              {editLoading ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
                            </button>
                            <button onClick={cancelEdit}
                              title="Cancelar"
                              className="w-8 h-8 rounded border border-ink-300 hover:bg-ink-50 flex items-center justify-center shrink-0">
                              <X className="size-3.5" />
                            </button>
                            {editMsg && (
                              <span className={`text-[10px] whitespace-nowrap ${editMsg.startsWith("Error") ? "text-rose-600" : "text-emerald-600"}`}>
                                {editMsg}
                              </span>
                            )}
                          </div>
                        ) : editandoPass ? (
                          /* Modo cambio de contraseña */
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newPass}
                              onChange={(e) => setNewPass(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") cambiarPassword(); if (e.key === "Escape") { setPassUserId(null); } }}
                              placeholder="Nueva contraseña"
                              className="w-36 border border-brand-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                              autoFocus
                            />
                            <button onClick={cambiarPassword} disabled={passLoading}
                              className="text-xs px-2 py-1.5 bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-40">
                              {passLoading ? "…" : "Guardar"}
                            </button>
                            <button onClick={() => { setPassUserId(null); setPassMsg(null); }}
                              className="text-xs px-2 py-1.5 border border-ink-300 rounded hover:bg-ink-50">
                              ✕
                            </button>
                            {passMsg && (
                              <span className={`text-xs ${passMsg.startsWith("Error") ? "text-rose-600" : "text-emerald-600"}`}>
                                {passMsg}
                              </span>
                            )}
                          </div>
                        ) : (
                          /* Botones normales */
                          <>
                            <button
                              onClick={() => startEdit(u)}
                              disabled={u.superAdmin && !actual?.superAdmin}
                              title="Editar nombre y rol"
                              className="text-xs px-2 py-1.5 border border-ink-200 text-ink-600 rounded hover:bg-ink-50 hover:border-brand-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              <Pencil className="size-3" /> Editar
                            </button>
                            <button
                              onClick={() => { setPassUserId(u.id); setNewPass(""); setPassMsg(null); setEditUserId(null); }}
                              disabled={u.superAdmin && !actual?.superAdmin}
                              title="Cambiar contraseña"
                              className="text-xs px-2 py-1.5 border border-brand-300 text-brand-700 rounded hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              <KeyRound className="size-3" /> Contraseña
                            </button>
                            <button
                              onClick={() => toggleActivo(u)}
                              disabled={esYo || u.superAdmin}
                              title={esYo ? "No podés desactivar tu propia cuenta" : u.superAdmin ? "No se puede desactivar al maestro" : ""}
                              className="text-xs px-2 py-1.5 border border-ink-300 rounded hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {u.isActive ? "Desactivar" : "Activar"}
                            </button>
                            <button
                              onClick={() => togglePermisosPanel(u)}
                              disabled={u.superAdmin && !actual?.superAdmin}
                              title="Forzar acceso a tarjetas puntuales para este usuario"
                              className="text-xs px-2 py-1.5 border border-violet-300 text-violet-700 rounded hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              <ShieldCheck className="size-3" /> Permisos
                              {permisosAbiertoId === u.id ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                            </button>
                            {esAdminVerdadero && !u.superAdmin && (
                              <button
                                onClick={() => toggleDelegacion(u)}
                                title="Delegar gestión de usuarios/permisos (mismo alcance que admin)"
                                className={`text-xs px-2 py-1.5 border rounded ${u.puedeGestionarPermisos ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-ink-300 text-ink-600 hover:bg-ink-50"}`}
                              >
                                {u.puedeGestionarPermisos ? "Delegado ✓" : "Delegar"}
                              </button>
                            )}
                            <button
                              onClick={() => eliminarUsuario(u)}
                              disabled={noPuedeEliminar}
                              title={
                                esYo ? "No podés eliminar tu propia cuenta"
                                : (!actorEsSuperAdmin && targetProtegido) ? "Solo el super admin puede eliminar a otro admin, gerente o supervisor"
                                : "Eliminar usuario"
                              }
                              className="text-xs px-2 py-1.5 border border-rose-300 text-rose-700 rounded hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              <Trash2 className="size-3" /> Eliminar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && users.map((u) => permisosAbiertoId === u.id ? (
                <tr key={`${u.id}-permisos`} className="bg-violet-50/40 border-b border-ink-100">
                  <td colSpan={7} className="px-4 py-4">
                    <p className="text-xs font-semibold text-violet-800 mb-1">
                      Permisos de {u.fullName} <span className="text-ink-400 font-normal">— por encima/por debajo de lo que su rol ({ROLE_LABEL[u.role] ?? u.role}) daría por defecto</span>
                    </p>
                    {permisosCargando ? (
                      <div className="text-xs text-ink-500 py-2 flex items-center gap-2"><Loader2 className="size-3 animate-spin" /> Cargando…</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 mt-2">
                        {CATALOGO_RUTAS.map(grupo => (
                          <div key={grupo.grupo}>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-ink-400 mb-1.5">{grupo.grupo}</p>
                            <div className="flex flex-col gap-1.5">
                              {grupo.items.map(item => {
                                const ov = overrideDe(item.ruta);
                                const valor = ov === undefined ? "heredado" : ov ? "si" : "no";
                                const guardando = permisosGuardandoRuta === item.ruta;
                                return (
                                  <div key={item.ruta} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-ink-700 truncate">{item.label}</span>
                                    <select
                                      value={valor}
                                      disabled={guardando}
                                      onChange={e => cambiarPermiso(u.id, item.ruta, e.target.value as any)}
                                      className={`text-[11px] border rounded px-1 py-0.5 shrink-0 ${
                                        valor === "si" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                        : valor === "no" ? "border-rose-300 bg-rose-50 text-rose-800"
                                        : "border-ink-200 text-ink-500"
                                      }`}
                                    >
                                      <option value="heredado">Hereda del rol</option>
                                      <option value="si">Forzar SÍ</option>
                                      <option value="no">Forzar NO</option>
                                    </select>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ) : null)}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-500">Sin usuarios.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
